/**
 * GlobalLoader — indicateur de chargement global de l'app.
 *
 * @remarks
 * Piloté par TanStack Query : visible tant qu'une query **sans donnée en
 * cache** est en fetch (`useIsFetching` filtré sur `status === 'pending'`).
 * Les mutations ne le déclenchent pas (SAAS-1269) : elles montrent leur retour
 * sur place.
 *
 * Les refetch de fond d'une donnée déjà en cache (`staleTime` dépassé,
 * `refetchOnWindowFocus`, `invalidateQueries` après mutation) ne déclenchent
 * **pas** le voile : l'écran affiche déjà la donnée, un loader par-dessus
 * serait un faux signal. Une donnée fraîche en cache ne fetch pas du tout,
 * donc rien ne s'affiche non plus.
 *
 * - **Anti-flicker** : affiché **immédiatement** dès qu'une activité démarre
 *   (pas de délai, sinon les loaders locaux apparaissent avant le voile), et
 *   maintenu au moins `MIN_VISIBLE_MS` même si la requête finit avant. Une
 *   requête de 80 ms montre donc le voile pendant `MIN_VISIBLE_MS`, jamais
 *   un flash de quelques frames.
 * - **Non bloquant** : `pointer-events-none`, l'utilisateur garde la main.
 *   Un overlay bloquant sur chaque refetch de fond serait insupportable.
 * - **Visuel** : symbole Alt en demi-rotations horaires successives
 *   (`halfSpinCw` dans `globals.css`).
 *
 * **Positionnement** : `absolute inset-0` — à monter dans un conteneur
 * `relative` qui délimite la zone à voiler. Desktop : le panneau central de
 * `PanelGroup` uniquement (header, sidebar et panneaux latéraux restent
 * visibles). Tablette / mobile : la zone de la vue centrale.
 */

import type { ReactNode } from 'react';
import { useIsVeiled } from '@/shared/ui/loader-veil-context';
import { AltLogo } from '@/shared/components';

/** Voile commun (fond translucide + flou), non bloquant, centré un peu haut. */
export function GlobalLoaderOverlay({ children }: { children: ReactNode }) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Chargement en cours"
      className="pointer-events-none absolute inset-0 z-(--z-overlay) flex animate-[fadeIn_150ms_ease-out] items-center justify-center bg-[color-mix(in_srgb,var(--color-bg)_70%,transparent)] pb-20 backdrop-blur-[2px]"
    >
      {children}
    </div>
  );
}

/** Variante 1 — symbole Alt en demi-rotations horaires. */
export function GlobalLoader() {
  const visible = useIsVeiled();
  if (!visible) return null;

  return (
    <GlobalLoaderOverlay>
      <AltLogo
        variant="symbol"
        alt=""
        className="size-24 animate-[halfSpinCw_1.4s_ease-in-out_infinite] motion-reduce:animate-none"
      />
    </GlobalLoaderOverlay>
  );
}
